
import { useCallback, useEffect, useState } from 'react';
import {
  getSimulationStatus,
  startSimulation,
  stopSimulation,
  clearSimulationData,
  type SimulationStatusResponse,
} from '@/lib/api';
import { Button, Input, ConfirmDialog } from '@/components/ui';

const POLL_INTERVAL_MS = 15000;
const DEFAULT_INTERVAL_MIN = 10;
const DEFAULT_INTERVAL_MAX = 60;

export function SimulationControls() {
  const [status, setStatus] = useState<SimulationStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [intervalMinSec, setIntervalMinSec] = useState(DEFAULT_INTERVAL_MIN);
  const [intervalMaxSec, setIntervalMaxSec] = useState(DEFAULT_INTERVAL_MAX);
  const [durationMin, setDurationMin] = useState<number | ''>('');
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [clearLoading, setClearLoading] = useState(false);

  const fetchStatus = useCallback(async () => {
    const { data, error: err } = await getSimulationStatus();
    if (err) {
      setError(err);
      setStatus(null);
    } else {
      setError(null);
      setStatus(data ?? null);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  useEffect(() => {
    if (!status?.running) return;
    const id = setInterval(fetchStatus, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [status?.running, fetchStatus]);

  const handleStart = async () => {
    setActionLoading(true);
    setError(null);
    const { data, error: err } = await startSimulation({
      intervalMinSec,
      intervalMaxSec,
      durationMin: durationMin === '' ? undefined : Number(durationMin),
    });
    setActionLoading(false);
    if (err) {
      setError(err);
      return;
    }
    if (data) {
      setStatus({ running: true, startedAt: data.startedAt });
    }
  };

  const handleStop = async () => {
    setActionLoading(true);
    setError(null);
    const { error: err } = await stopSimulation();
    setActionLoading(false);
    if (err) {
      setError(err);
      return;
    }
    setStatus({ running: false, startedAt: null });
  };

  const handleClearConfirm = async () => {
    setClearLoading(true);
    setError(null);
    const { error: err } = await clearSimulationData();
    setClearLoading(false);
    setShowClearConfirm(false);
    if (err) {
      setError(err);
      return;
    }
    setError(null);
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-10 bg-slate-200 dark:bg-slate-700 rounded" />
        <div className="h-20 bg-slate-200 dark:bg-slate-700 rounded" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {error && (
        <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 px-3 py-2 rounded">
          {error}
        </div>
      )}

      {status?.running ? (
        <>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Simulation running — requests will appear at random intervals in Submissions and Live Queue.
          </p>
          {status.startedAt && (
            <p className="text-xs text-slate-500 dark:text-slate-500">
              Started {new Date(status.startedAt).toLocaleString()}
            </p>
          )}
          <Button
            onClick={handleStop}
            disabled={actionLoading}
            variant="outline"
          >
            {actionLoading ? 'Stopping…' : 'Stop simulation'}
          </Button>
        </>
      ) : (
        <>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Generate practice submissions at random intervals so moderators and producers can practice.
          </p>
          <div className="grid grid-cols-2 gap-3 max-w-md">
            <Input
              type="number"
              label="Min interval (sec)"
              min={1}
              max={300}
              value={intervalMinSec}
              onChange={(e) => setIntervalMinSec(Number(e.target.value) || 1)}
            />
            <Input
              type="number"
              label="Max interval (sec)"
              min={1}
              max={600}
              value={intervalMaxSec}
              onChange={(e) => setIntervalMaxSec(Number(e.target.value) || 1)}
            />
          </div>
          <div className="max-w-md">
            <Input
              type="number"
              label="Duration (min, optional)"
              placeholder="Until stopped"
              min={1}
              max={120}
              value={durationMin === '' ? '' : durationMin}
              onChange={(e) => setDurationMin(e.target.value === '' ? '' : Number(e.target.value))}
            />
          </div>
          <Button
            onClick={handleStart}
            disabled={actionLoading || intervalMinSec > intervalMaxSec}
          >
            {actionLoading ? 'Starting…' : 'Start practice simulation'}
          </Button>
        </>
      )}

      <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
        <Button
          variant="outline"
          onClick={() => setShowClearConfirm(true)}
          className="text-slate-700 dark:text-slate-300 border-slate-300 dark:border-slate-600"
        >
          Clear practice submissions
        </Button>
      </div>

      <ConfirmDialog
        isOpen={showClearConfirm}
        onClose={() => setShowClearConfirm(false)}
        onConfirm={handleClearConfirm}
        title="Clear practice submissions"
        message="Delete all simulated prayers? This cannot be undone."
        confirmLabel="Delete all"
        variant="danger"
        isLoading={clearLoading}
      />
    </div>
  );
}
